import { type FC, useCallback, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { type ThreeEvent } from '@react-three/fiber';
import { Line } from '@react-three/drei';
import { ViewerLayer } from './ViewerLayer';
import { TextBillboard, type TextBillboardProps } from './TextBillboard';
import { useOnClick } from './useOnClick';

// eslint-disable-next-line react-refresh/only-export-components
export enum MarkerShapeType {
  SPHERE = 0,
  CYLINDER = 1,
  FLAG = 2,
}

export interface MarkerShapeStyle {
  color: string | THREE.Color;
  opacity: number;
  size: number;
}

export interface MarkerProps {
  id: string;
  text?: string;
  shapeType?: MarkerShapeType;
  style: MarkerShapeStyle;
  highlightedStyle?: MarkerShapeStyle;
  billboard?: Omit<TextBillboardProps, 'text' | 'position'>;
  position: Parameters<THREE.Vector3['set']>;
  rotation?: Parameters<THREE.Euler['set']>;
  showBillboard?: boolean;
  onMarkerClick?: (markerId: string, button: number) => void;
}

export const Marker: FC<MarkerProps> = ({
  id,
  text,
  shapeType,
  style,
  highlightedStyle,
  billboard,
  position,
  rotation,
  showBillboard,
  onMarkerClick,
}) => {
  const mesh = useRef<THREE.Mesh | null>(null);
  const root = useRef<THREE.Group | null>(null);

  const [isHighlighted, setIsHighlighted] = useState(false);

  const handlePointerOver = () => {
    setIsHighlighted(true);
  };

  const handlePointerOut = () => {
    setIsHighlighted(false);
  };

  const shouldHandle = useCallback((event: ThreeEvent<MouseEvent>) => {
    const { altKey, ctrlKey, metaKey, shiftKey } = event;
    if (altKey || ctrlKey || metaKey || shiftKey) {
      return false;
    }
    return true;
  }, []);

  const onClick = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      if (onMarkerClick) {
        onMarkerClick(id, event.nativeEvent.button);
      }
    },
    [onMarkerClick, id]
  );

  const { onPointerDown, onPointerUp } = useOnClick({ onClick, shouldHandle });

  const { color, opacity, size } = isHighlighted ? highlightedStyle || style : style;

  const meshCallback = useCallback((node: THREE.Mesh | null) => {
    if (node != null) {
      const { layers } = node;
      layers.enable(ViewerLayer.SphericalViewerLayer);
      layers.enable(ViewerLayer.AutoPickable);
      mesh.current = node;
    }
  }, []);

  const MarkerMesh = useMemo(() => {
    const DisplayBillboard = showBillboard && isHighlighted && billboard && text && (
      <TextBillboard position={[0, 0, 2]} text={text} {...billboard} />
    );
    switch (shapeType) {
      case MarkerShapeType.CYLINDER:
        return (
          <>
            {DisplayBillboard}
            <cylinderGeometry attach="geometry" args={[1, 1, 0.25, 32]} />
          </>
        );

      case MarkerShapeType.SPHERE:
        return (
          <>
            {DisplayBillboard}
            <sphereGeometry attach="geometry" args={[1, 32, 16]} />
          </>
        );

      case MarkerShapeType.FLAG:
        return (
          <>
            {/*
              Top board, wrapped inside the mesh
              Clicking on this would trigger mesh click
             */}
            {billboard && text ? (
              <TextBillboard position={[0, 0, 3]} text={text} {...billboard} clickable />
            ) : (
              <></>
            )}
            {/* Sphere mesh */}
            <sphereGeometry attach="geometry" args={[0.5]} />
            {/* A line to connect both */}
            <Line
              points={[new THREE.Vector3(0, 0, 0.5), new THREE.Vector3(0, 0, 2.5)]}
              color={0xffffff}
              lineWidth={1}
              dashed={false}
              forceSinglePass={false}
            />
          </>
        );

      default:
        return <></>;
    }
  }, [billboard, isHighlighted, shapeType, showBillboard, text]);

  return (
    <group ref={root} position={position}>
      <mesh
        ref={meshCallback}
        position={[0, 0, 0]}
        scale={[size, size, size]}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        rotation={rotation}
      >
        {MarkerMesh}
        <meshPhongMaterial
          attach="material"
          color={color}
          transparent
          opacity={opacity}
          flatShading
          emissive={color} // marker will be unaffected by lighting
        />
      </mesh>
    </group>
  );
};
